import api from "./api";
import { parsePaginatedList } from "../utils/pagination";

export interface SaleItem {
  id: string;
  saleId: string;
  productId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  kotNumber?: string | null;
  kotStatus?: string | null;
  createdAt: string;
  updatedAt: string;
  product?: {
    id: string;
    name: string;
    sku: string;
  };
}

export interface Sale {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  totalAmount: number;
  discountAmount: number;
  taxAmount: number;
  netAmount: number;
  paymentMethod: string;
  status: "pending" | "completed" | "cancelled";
  branchId: string;
  salesPointId?: string | null;
  createdAt: string;
  updatedAt: string;
  branch?: {
    id: string;
    name: string;
  };
  items?: SaleItem[];
  saleItems?: SaleItem[];
}

export interface SaleLineItemInput {
  productId: string;
  quantity: number;
  unitPrice?: number;
  totalPrice?: number;
  netAmount?: number;
  discountAmount?: number;
  notes?: string;
}

export interface CreateSaleData {
  branchId: string;
  salesPointId?: string;
  paymentMethod?: string;
  orderType?: "DINE_IN" | "TAKEAWAY" | "DELIVERY";
  tableId?: string | null;
  notes?: string;
}

export interface SyncCompleteSaleData {
  saleNumber: string;
  branchId: string;
  salesPointId: string;
  paymentMethod: string;
  totalAmount: number;
  taxAmount?: number;
  discountAmount?: number;
  netAmount: number;
  status?: "pending" | "completed" | "cancelled";
  notes?: string;
  cardNumber?: string;
  customerName?: string;
  customerId?: string;
  orderNotes?: string;
  items: SaleLineItemInput[];
}

export interface CompleteSaleData {
  paymentMethod: string;
  discountAmount?: number;
  taxAmount?: number;
  cardHolderId?: string;
  nfcCardId?: string;
  customerId?: string | null;
  customerName?: string;
}

export interface AddKotData {
  items: SaleLineItemInput[];
  notes?: string;
}

export interface UpdateSaleData {
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  paymentMethod?: string;
  status?: "pending" | "completed" | "cancelled";
}

const saleAPI = {
  // Get all sales with pagination
  getSales: async (params?: {
    pageSize?: number;
    pageNo?: number;
    branchId?: string;
    cardHolderId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    customerName?: string;
  }): Promise<{ data: Sale[]; total: number }> => {
    const response = await api.get("/sales", { params });
    const { data, total } = parsePaginatedList<Sale>(response.data.data);
    return { data, total };
  },

  // Get sale by ID
  getSaleById: async (id: string): Promise<Sale> => {
    const response = await api.get(`/sales/${id}`);
    return response.data.data;
  },

  // Open a sale session (restaurant table / takeaway)
  createSale: async (data: CreateSaleData): Promise<Sale> => {
    const response = await api.post("/sales", data);
    return response.data.data;
  },

  syncCompleteSale: async (data: SyncCompleteSaleData): Promise<Sale> => {
    const response = await api.post("/sales/sync-complete", data);
    return response.data.data;
  },

  addKot: async (
    saleId: string,
    data: AddKotData
  ): Promise<{ kotNumber: string | null; items: unknown[] }> => {
    const response = await api.post(`/sales/${saleId}/kot`, data);
    return response.data.data;
  },

  completeSale: async (id: string, data: CompleteSaleData): Promise<Sale> => {
    const response = await api.patch(`/sales/${id}/complete`, data);
    return response.data.data;
  },

  // Update sale
  updateSale: async (id: string, data: UpdateSaleData): Promise<Sale> => {
    const response = await api.put(`/sales/${id}`, data);
    return response.data.data;
  },

  // Cancel sale with reason (soft cancel — not deleted from records)
  cancelSale: async (id: string, reason: string): Promise<Sale> => {
    const response = await api.post(`/sales/${id}/cancel`, { reason });
    return response.data.data?.sale || response.data.data;
  },

  // Delete mistaken sale (legacy — prefer cancelSale)
  deleteSale: async (id: string, reason?: string): Promise<void> => {
    await api.delete(`/sales/${id}`, {
      data: reason ? { reason } : undefined,
    });
  },

  // Get sales by branch
  getSalesByBranch: async (
    branchId: string
  ): Promise<{ data: Sale[]; total: number }> => {
    const response = await api.get(`/sales/branch/${branchId}`);
    return {
      data: response.data.data,
      total: response.data.data.length,
    };
  },

  // Get sales report
  getSalesReport: async (params?: {
    startDate?: string;
    endDate?: string;
    branchId?: string;
  }): Promise<any> => {
    const response = await api.get("/sales/report", { params });
    return response.data.data;
  },

  // Generate invoice for a sale
  generateInvoice: async (
    id: string
  ): Promise<{ sale: Sale; invoiceDate: string; invoiceNumber: string }> => {
    const response = await api.get(`/sales/${id}/invoice`);
    return response.data.data;
  },

  // Refund a sale
  refundSale: async (
    id: string,
    data: {
      refundAmount?: number;
      reason: string;
      refundItems?: { saleItemId: string; quantity: number }[];
    }
  ): Promise<Sale> => {
    const response = await api.post(`/sales/${id}/refund`, data);
    return response.data.data;
  },

  // Get today's sales
  getTodaySales: async (params?: {
    pageNo?: number;
    pageSize?: number;
  }): Promise<{ data: Sale[]; total: number }> => {
    const response = await api.get("/sales/today", { params });
    return {
      data: response.data.data,
      total: response.data.data.length,
    };
  },

  // Get sales by salesPoint (deprecated - use branch-based filtering)
  getSalesBySalesPoint: async (
    salesPointId: string,
    params?: { pageNo?: number; pageSize?: number }
  ): Promise<{ data: Sale[]; total: number }> => {
    const response = await api.get(`/sales/salesPoint/${salesPointId}`, {
      params,
    });
    return {
      data: response.data.data,
      total: response.data.data.length,
    };
  },
};

export { saleAPI };
